import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  IconButton,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { LeadStatusSelect } from '../leads/status';
import { DynamicFormFields } from '../dynamicFields/DynamicFormFields';
import { useCustomColumns } from '../../hooks/leads/useCustomColumns';
import { LeadDetailsView } from '../leads/LeadDetailsView';
import { CustomColumnDefinition } from '../../types/lead';
import { parseCustomFields } from '../../utils/leads/leadsUtils';

interface UpdateExistingLeadDialogProps {
  open: boolean;
  existingLead: any;
  inputData: any;
  onConfirm: (updates: Record<string, any>) => void;
  onCancel: () => void;
  isLoading: boolean;
  mode?: 'conflict' | 'edit' | 'view';
  smbId?: string;
  customStatuses?: string[];
  setCustomStatuses?: React.Dispatch<React.SetStateAction<string[]>>;
  onStatusUpdated?: (res?: any, createdStatus?: string) => void;
  customColumns?: CustomColumnDefinition[];
}

export const UpdateExistingLeadDialog: React.FC<UpdateExistingLeadDialogProps> = ({
  open,
  existingLead,
  inputData,
  onConfirm,
  onCancel,
  isLoading,
  mode = 'conflict',
  smbId,
  customStatuses,
  setCustomStatuses,
  onStatusUpdated,
  customColumns: customColumnsProp,
}) => {
  const [editableName, setEditableName] = useState(inputData?.name || '');
  const [status, setStatus] = useState(
    mode === 'conflict' && inputData?.status !== undefined ? inputData.status : (existingLead?.status || '')
  );
  const [notes, setNotes] = useState(
    mode === 'conflict' && inputData?.notes !== undefined ? inputData.notes : (existingLead?.notes || '')
  );
  const [customFields, setCustomFields] = useState<Record<string, any>>(() => {
    const existing = parseCustomFields(existingLead?.custom_fields);
    const input = mode === 'conflict' && inputData?.custom_fields ? parseCustomFields(inputData.custom_fields) : {};
    return { ...existing, ...input };
  });
  const [isEditing, setIsEditing] = useState(mode !== 'view');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { customColumns: fetchedCustomColumns, addOptionToColumn } = useCustomColumns(smbId);
  const customColumns = customColumnsProp || fetchedCustomColumns;

  const prevOpenRef = React.useRef(false);
  const prevLeadIdRef = React.useRef<string | undefined>(undefined);

  useEffect(() => {
    if (open && (!prevOpenRef.current || existingLead?.id !== prevLeadIdRef.current)) {
      setIsEditing(mode !== 'view');
      setEditableName(inputData?.name || '');
      setStatus(mode === 'conflict' && inputData?.status !== undefined ? inputData.status : (existingLead?.status || ''));
      setNotes(mode === 'conflict' && inputData?.notes !== undefined ? inputData.notes : (existingLead?.notes || ''));
      const existing = parseCustomFields(existingLead?.custom_fields);
      const input = mode === 'conflict' && inputData?.custom_fields ? parseCustomFields(inputData.custom_fields) : {};
      setCustomFields({ ...existing, ...input });
    }
    prevOpenRef.current = open;
    prevLeadIdRef.current = existingLead?.id;
  }, [open, existingLead?.id, mode]);

  if (!existingLead || !inputData) return null;

  const getUpdatesPayload = () => {
    const updates: Record<string, any> = {};
    const trimmedName = editableName.trim();
    const existingName = (existingLead?.name || '').trim();
    if (trimmedName !== existingName) updates.name = trimmedName;

    if (status && status !== (existingLead?.status || '')) updates.lead_status = status;
    if ((notes || '').trim() !== (existingLead?.notes || '').trim()) updates.notes = (notes || '').trim();
    // Key-level diffing for custom_fields (sends only changed keys)
    const existingCustomFields = parseCustomFields(existingLead?.custom_fields);
    const currentCustomFields = customFields || {};
    const changedCustomFields: Record<string, any> = {};
    let hasCustomFieldsChanged = false;

    for (const key of Object.keys(currentCustomFields)) {
      if (JSON.stringify(currentCustomFields[key]) !== JSON.stringify(existingCustomFields[key])) {
        changedCustomFields[key] = currentCustomFields[key];
        hasCustomFieldsChanged = true;
      }
    }
    for (const key of Object.keys(existingCustomFields)) {
      if (!(key in currentCustomFields)) {
        changedCustomFields[key] = null;
        hasCustomFieldsChanged = true;
      }
    }
    if (hasCustomFieldsChanged) {
      updates.custom_fields = changedCustomFields;
    }

    return updates;
  };

  const updatesPayload = getUpdatesPayload();
  const isDirty = Object.keys(updatesPayload).length > 0;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {!isEditing
              ? 'Lead Details'
              : mode === 'edit' || mode === 'view'
              ? 'Edit Lead'
              : 'Lead Already Exists'}
          </Typography>
          <Box>
            {mode === 'view' && (
              <IconButton
                onClick={() => {
                  if (isEditing) {
                    setIsEditing(false);
                    setEditableName(existingLead.name || '');
                    setStatus(existingLead.status || 'New');
                    setNotes(existingLead.notes || '');
                    setCustomFields(existingLead.custom_fields || {});
                  } else {
                    setIsEditing(true);
                  }
                }}
                disabled={isLoading}
                size="small"
                sx={{ mr: 1 }}
                color={isEditing ? 'primary' : 'default'}
              >
                <EditIcon />
              </IconButton>
            )}
            <IconButton onClick={onCancel} disabled={isLoading} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {mode === 'conflict' && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            A lead with this phone number already exists. If you want, you can update their details below.
          </Alert>
        )}

        {!isEditing ? (
          <LeadDetailsView
            existingLead={existingLead}
            notes={notes}
            customFields={customFields}
            customColumns={customColumns}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Lead Name"
              value={editableName}
              onChange={(e) => setEditableName(e.target.value)}
              autoFocus
              FormHelperTextProps={{
                sx: { wordBreak: 'break-word', overflowWrap: 'anywhere' }
              }}
              helperText={
                mode === 'conflict' && existingLead?.name?.trim()
                  ? `Currently saved as: ${existingLead.name}${!editableName.trim() ? '. (If left empty, phone number will be used for display)' : ''}`
                  : (!editableName.trim() ? 'If left empty, phone number will be used for display' : '')
              }
            />

            <TextField
              fullWidth
              label="Phone Number"
              value={existingLead.phone ? (existingLead.phone.startsWith('+') ? existingLead.phone : `+${existingLead.phone}`) : ''}
              disabled
              InputProps={{
                readOnly: true,
              }}
            />

            <LeadStatusSelect
              smbId={smbId}
              value={status}
              onChange={(newStatus) => setStatus(newStatus)}
              customStatuses={customStatuses}
              setCustomStatuses={setCustomStatuses}
              onStatusUpdated={onStatusUpdated}
              helperText={
                mode === 'conflict' && existingLead?.status
                  ? `Currently saved as: ${existingLead.status}`
                  : undefined
              }
              FormHelperTextProps={{
                sx: { wordBreak: 'break-word', overflowWrap: 'anywhere' }
              }}
            />

            <DynamicFormFields
              customColumns={customColumns}
              notes={notes}
              customFields={customFields}
              onNotesChange={(n) => setNotes(n)}
              onCustomFieldChange={(key, val) =>
                setCustomFields((prev) => ({ ...prev, [key]: val }))
              }
              onAddOption={addOptionToColumn}
              mode={mode}
              existingNotes={existingLead?.notes}
              existingCustomFields={existingLead?.custom_fields}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
        {isEditing ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onCancel} disabled={isLoading}>
              {mode === 'edit' || mode === 'view' ? 'Cancel' : 'Go Back'}
            </Button>
            <Button
              onClick={() => onConfirm(updatesPayload)}
              variant="contained"
              disabled={isLoading || !isDirty}
            >
              {isLoading ? 'Updating...' : 'Update Lead'}
            </Button>
          </Box>
        ) : (
          <Button onClick={onCancel} variant="contained">
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
